import { Box, Button, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useGetProjectQuery } from 'api/project/queries/useGetProjectQuery';
import { useGetProjectBIMsQuery } from 'api/project/queries/useGetProjectBIMsQuery';
import useDeleteBIMMutation from 'api/project/mutations/useDeleteBIMMutation';
import { useWorkspaces } from 'contexts/WorkspacesContext';
import { BIM } from 'utils/bim';
import { checkAccess } from 'utils/userAccess';
import CreateBIMModal from './CreateBIMModal';
import { useAtom } from 'jotai';
import { staffVisionEnabledAtom } from 'store/atoms/staff/staffAtoms';
import JSONAccordion from 'components/JSONAccordion/JSONAccordion';

export default function BIMs() {
  const { projectSlug = '' } = useParams();
  const { activeWorkspace } = useWorkspaces();

  const [createBIMModalOpen, setCreateBIMModalOpen] = useState(false);
  const [staffVisionEnabled] = useAtom(staffVisionEnabledAtom);

  const { data: project, isPending: isProjectPending } = useGetProjectQuery(projectSlug, activeWorkspace.id);
  const { data: bims, isPending: isBIMsPending } = useGetProjectBIMsQuery(project?.id || "");
  const { mutate: deleteBIM, isPending: isDeletePending } = useDeleteBIMMutation(project?.id || "");

  const handleDeleteBIM = (bimId: string) => {
    if (isDeletePending) return;
    deleteBIM({ id: bimId });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={3} justifyContent="space-between" width="100%">
        <Stack direction="row" spacing={1} alignItems="center">
          <Link to=".." relative="path" style={{ textDecoration: 'none' }}>
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '1.25rem',
                letterSpacing: '-2%',
                color: '#777',
                transition: 'all .2s ease',
                ':hover': {
                  color: 'white',
                },
              }}
            >
              {project?.name}
            </Typography>
          </Link>
          <Icon icon="icon-park-outline:right" color="#aaa" />
          <Typography
            sx={{
              fontWeight: '600',
              fontSize: '1.25rem',
              letterSpacing: '-2%',
            }}
          >
            BIMs
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {checkAccess("ADMIN", activeWorkspace.role) && (
            <Button
              variant="contained"
              onClick={() => setCreateBIMModalOpen(true)}
              sx={{
                background: 'royalblue',
                color: 'white',
                fontWeight: 600,
                ':hover': {
                  background: 'royalblue',
                  opacity: 0.9,
                },
              }}
            >
              Add BIM
            </Button>
          )}
        </Stack>
      </Stack>

      {staffVisionEnabled && (
        <JSONAccordion data={bims} />
      )}

      {isProjectPending || isBIMsPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : !bims || bims.length === 0 ? (
        <Box sx={{
          display: 'grid',
          placeItems: 'center',
          padding: '4rem',
          height: '100%',
          gap: "1.5rem"
        }}>
          <Icon icon="mdi:file-3d-outline" width="4rem" height="4rem" color="#666" />
          <Typography textAlign="center" variant="h5" color="#666">
            No BIM found
          </Typography>
          <Typography textAlign="center" variant="body2" color="#888">
            Upload your first BIM model to get started
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Scale</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bims.map((bim: BIM) => (
                <TableRow key={bim.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon="mdi:file-3d-outline" width="1.5rem" height="1.5rem" color="#666" />
                      <Typography variant="body2" fontWeight={600}>
                        {bim.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bim.isInAutodesk ? "Autodesk" : "Local"}
                      size="small"
                      color={bim.isInAutodesk ? "primary" : "default"}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bim.scale}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(bim.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleDeleteBIM(bim.id)}
                      disabled={isDeletePending}
                      color="error"
                      size="small"
                    >
                      {isDeletePending ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <Icon icon="ph:trash" width="1rem" height="1rem" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateBIMModal 
        open={createBIMModalOpen} 
        onClose={() => setCreateBIMModalOpen(false)} 
      />
    </Box>
  );
}
